import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * EnablePushNotifications — single button that subscribes the browser to web push.
 * - Hides itself if Notification / PushManager / service worker isn't supported.
 * - Reflects the current permission/subscription state and re-renders accordingly.
 */

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

const supportsPush = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export default function EnablePushNotifications() {
  const [state, setState] = useState("idle"); // idle | enabled | denied | unsupported | working
  const [vapidKey, setVapidKey] = useState("");

  useEffect(() => {
    if (!supportsPush()) {
      setState("unsupported");
      return;
    }
    if (Notification.permission === "denied") {
      setState("denied");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/push/public-key");
        if (data?.key) setVapidKey(data.key);
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) setState("enabled");
        else setState("idle");
      } catch {
        setState("idle");
      }
    })();
  }, []);

  const enable = async () => {
    if (!supportsPush()) return;
    setState("working");
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Notifications blocked. Enable them in your browser settings.");
        setState(perm === "denied" ? "denied" : "idle");
        return;
      }
      let key = vapidKey;
      if (!key) {
        const { data } = await api.get("/push/public-key");
        key = data?.key;
        setVapidKey(key);
      }
      if (!key) {
        toast.error("Push not configured on server.");
        setState("idle");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
      });
      toast.success("Phone alerts enabled.");
      setState("enabled");
    } catch (err) {
      console.warn(err);
      toast.error("Could not enable phone alerts.");
      setState("idle");
    }
  };

  const disable = async () => {
    setState("working");
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const json = sub.toJSON();
        await api.post("/push/unsubscribe", { endpoint: json.endpoint });
        await sub.unsubscribe();
      }
      toast.success("Phone alerts turned off.");
      setState("idle");
    } catch (err) {
      console.warn(err);
      setState("enabled");
    }
  };

  if (state === "unsupported") return null;

  if (state === "denied") {
    return (
      <div data-testid="push-denied" className="inline-flex items-center gap-2 text-xs text-stone-500 font-sans">
        <BellOff size={14} /> Phone alerts blocked in browser settings
      </div>
    );
  }

  if (state === "enabled") {
    return (
      <Button
        data-testid="push-disable"
        size="sm"
        variant="outline"
        onClick={disable}
        className="border-emerald-700/40 text-emerald-800 hover:bg-emerald-50"
      >
        <BellRing size={14} className="mr-1.5" /> Phone alerts on
      </Button>
    );
  }

  return (
    <Button
      data-testid="push-enable"
      size="sm"
      onClick={enable}
      disabled={state === "working"}
      className="bg-emerald-800 hover:bg-emerald-700 text-stone-50"
    >
      <Bell size={14} className="mr-1.5" />
      {state === "working" ? "Enabling…" : "Enable phone alerts"}
    </Button>
  );
}
